import {
  ImacOutlineIcon,
  ShareOutlineIcon,
  SlideshowOutlineIcon,
} from '@carboncopyco/icons';
import {
  ColorHex,
  Paper,
  TextBase,
  TextButton,
  TextCaption,
  TooltipBubble,
} from '@carboncopyco/ui';
import {
  dateUTCFromNow,
  integerWithCommas,
  integersFormatter,
} from '@carboncopyco/utils';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import type React from 'react';
import { Screenshot } from '../Screenshot';

interface SearchResultTileProps {
  title: string;
  image?: string;
  lastUpdated: string;
  totalShares?: number;
  totalPresentations?: number;
  icon: React.ReactNode;
  ActionDropdown: React.FC<{
    isMenuOpen: boolean;
    toggleMenu: () => void;
  }>;
  isDesktop?: boolean;
  onTileClick?: (params: unknown) => void;
  isOverlayDemo?: boolean;
  isOverlayTour?: boolean;
  isPending?: boolean;
  isPlaybook?: boolean;
}

export const SearchResultTile = ({
  icon,
  ActionDropdown,
  title,
  lastUpdated,
  image,
  totalPresentations,
  totalShares,
  isDesktop,
  onTileClick,
  isOverlayDemo,
  isOverlayTour,
  isPending,
  isPlaybook,
}: SearchResultTileProps) => {
  const [isHover, setHover] = useBoolean(false);
  const [isMenuOpen, { toggle }] = useBoolean(false);
  const active = isHover || isMenuOpen;

  return (
    <Paper radius>
      <div
        className={classNames(
          'w-full border border-1 border-ds-natural-150 rounded-lg hover:shadow-md',
          { 'cursor-pointer': !isPending && onTileClick },
          { 'pointer-events-none': isPending }
        )}
        onMouseEnter={setHover.setTrue}
        onMouseLeave={setHover.setFalse}
        onClick={onTileClick}
      >
        <AspectRatio ratio={16 / 10}>
          <div
            className={classNames('w-full h-full', {
              'opacity-10': isPending,
            })}
          >
            <Screenshot
              rounded="lg"
              roundedTop
              noBorder
              image={image}
              alt="screenshot"
              isOverlayDemo={isOverlayDemo}
              isOverlayTour={isOverlayTour}
              isPlaybook={isPlaybook}
            />
          </div>
          <div
            className={classNames(
              'top-0 h-full z-20 absolute w-full flex rounded-md',
              {
                visible: active,
                invisible: !active,
              }
            )}
          >
            <div className="absolute inset-0 bg-ds-natural-700-a rounded-t-md" />
            {!isPending && (
              <div className="absolute right-0 m-1">
                <ActionDropdown
                  isMenuOpen={isMenuOpen}
                  toggleMenu={toggle}
                />
              </div>
            )}
          </div>
          {isPending && (
            <div className="top-0 absolute h-full w-full flex flex-col justify-center items-center">
              <TextBase
                color="text-ds-natural-600"
                label="Optimizing clone..."
              />
              <TextCaption label="Usually takes up to 1 minute" />
              <div className="absolute bottom-0 w-full">
                <div className="h-1 w-full bg-ds-primary-50 overflow-hidden">
                  <div className="animate-progressRun w-full h-full bg-ds-primary transform origin-left" />
                </div>
              </div>
            </div>
          )}
        </AspectRatio>
        <div
          className={`w-full flex items-center p-3 border-t border-ds-natural-150 ${
            isPending && 'opacity-20'
          }`}
        >
          <div className="w-full">
            <div className="flex items-center">
              <div className="flex w-full items-center">
                {icon}
                <div
                  className={classNames({
                    'max-w-[210px]': isDesktop,
                    'max-w-[242px]': !isDesktop,
                  })}
                >
                  <TooltipBubble
                    placement="top-start"
                    title={title}
                  >
                    <div className="text-ds-natural-700 ml-2 truncate pr-2">
                      <TextButton
                        ellipsis
                        label={title}
                        color="text-ds-natural-700"
                      />
                    </div>
                  </TooltipBubble>
                </div>
                <div
                  className={classNames({
                    hidden: !isDesktop,
                    'flex justify-end w-full': isDesktop,
                  })}
                >
                  <TooltipBubble
                    placement="top"
                    title="Created in the desktop app"
                    disabled={!isDesktop}
                  >
                    <div className="rounded-full bg-ds-natural-100 p-1.5">
                      <ImacOutlineIcon
                        size={12}
                        color={ColorHex.natural500}
                      />
                    </div>
                  </TooltipBubble>
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center mt-3">
              <TooltipBubble
                placement="top-start"
                title={`Edited ${dateUTCFromNow(lastUpdated)}`}
              >
                <div>
                  <TextCaption
                    ellipsis
                    label={`Edited ${dateUTCFromNow(lastUpdated)}`}
                    color="text-ds-natural-300"
                  />
                </div>
              </TooltipBubble>
              {totalShares || totalPresentations ? (
                <div className="flex">
                  {!!totalPresentations && (
                    <div className="flex items-center gap-1 mr-2">
                      <TooltipBubble
                        placement="top"
                        title="Presentations"
                      >
                        <div className="mt-1">
                          <SlideshowOutlineIcon
                            size={18}
                            color={ColorHex.natural300}
                          />
                        </div>
                      </TooltipBubble>
                      <TooltipBubble
                        disabled={totalPresentations < 10000}
                        placement="top"
                        title={integerWithCommas(totalPresentations)}
                      >
                        <TextCaption
                          ellipsis
                          label={integersFormatter(totalPresentations)}
                          color="text-ds-natural-300"
                        />
                      </TooltipBubble>
                    </div>
                  )}
                  {!!totalShares && (
                    <div className="flex items-center gap-1">
                      <TooltipBubble
                        placement="top"
                        title="Shares"
                      >
                        <div className="mt-1">
                          <ShareOutlineIcon
                            size={16}
                            color={ColorHex.natural300}
                          />
                        </div>
                      </TooltipBubble>
                      <TooltipBubble
                        disabled={totalShares < 10000}
                        placement="top"
                        title={integerWithCommas(totalShares)}
                      >
                        <TextCaption
                          ellipsis
                          label={integersFormatter(totalShares)}
                          color="text-ds-natural-300"
                        />
                      </TooltipBubble>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Paper>
  );
};
